import { HttpReadyState } from 'owa-http-status-codes';
import * as Constants from './Constants';

// Request error code
const ERROR_REQUEST_ERROR = 'RequestError';
const ERROR_REQUEST_TIMEOUT = 'RequestTimeout';
const ERROR_REQUEST_ABORT = 'RequestAbort';

// Image error code
const ERROR_IMAGE_ZEROBYTES = 'ImageZeroBytes';
const ERROR_IMAGE_INVALIDLENGTH = 'ImageInvalidLength';

/**
 * The image signature
 */
interface ImageSignature {
    format: string;
    signature: Uint8Array;
    mime: RegExp;
    expectedMime?: string;
}

/**
 * Response data
 */
export interface ResponseData {
    status: string;
    responseCode?: number;
    contentType?: string;
    detectedFormat?: string;
    first10BytesBase64?: string;
    mimeMatched?: boolean;
    expectedMime?: string;
}

/**
 * Image signature list
 * The signature comes from https://filesignatures.net
 * MIME comes from https://www.freeformatter.com/mime-types-list.html
 */
const imageSignatures: ImageSignature[] = [
    {
        format: 'JPEG',
        signature: new Uint8Array([0xff, 0xd8, 0xff, 0xe0]),
        mime: /^image\/p?jpe?g$/i,
        expectedMime: 'image/jpeg',
    },
    {
        format: 'EXIF',
        signature: new Uint8Array([0xff, 0xd8, 0xff, 0xe1]),
        mime: /^image\/p?jpe?g$/i,
    },
    {
        format: 'SPIFF',
        signature: new Uint8Array([0xff, 0xd8, 0xff, 0xe8]),
        mime: /^image\/p?jpe?g$/i,
    },
    {
        format: 'CannonEOS',
        signature: new Uint8Array([0xff, 0xd8, 0xff, 0xe2]),
        mime: /^image\/p?jpe?g$/i,
    },
    {
        format: 'SamsungD500',
        signature: new Uint8Array([0xff, 0xd8, 0xff, 0xe3]),
        mime: /^image\/p?jpe?g$/i,
    },
    {
        format: 'PNG',
        signature: new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
        mime: /^image\/png$/i,
        expectedMime: 'image/png',
    },
    {
        format: 'GIF',
        signature: new Uint8Array([0x47, 0x49, 0x46, 0x38]),
        mime: /^image\/gif$/i,
        expectedMime: 'image/gif',
    },
    {
        format: 'TIFF1',
        signature: new Uint8Array([0x49, 0x20, 0x49]),
        mime: /^image\/(x-)?tiff$/i,
    },
    {
        format: 'TIFF2',
        signature: new Uint8Array([0x49, 0x49, 0x2a, 0x00]),
        mime: /^image\/(x-)?tiff$/i,
    },
    {
        format: 'TIFF3',
        signature: new Uint8Array([0x4d, 0x4d, 0x00, 0x2a]),
        mime: /^image\/(x-)?tiff$/i,
    },
    {
        format: 'TIFF4',
        signature: new Uint8Array([0x4d, 0x4d, 0x00, 0x2b]),
        mime: /^image\/(x-)?tiff$/i,
    },
    {
        format: 'BMP',
        signature: new Uint8Array([0x42, 0x4d]),
        mime: /^image\/bmp$/i,
    },
];

/**
 * Make an xmlhttp request for a url to get a bit more information about the failure reason (i.e. http status code)
 * @param url The url
 */
export default function getUrlResponseData(
    url: string,
    withCredentials: boolean = false
): Promise<ResponseData> {
    return new Promise(resolve => {
        const request: XMLHttpRequest = new XMLHttpRequest();
        request.onreadystatechange = function () {
            if (request.readyState === HttpReadyState.Done) {
                const responseCode = request.status;
                let contentType = request.getResponseHeader('Content-Type') || '';
                if (contentType.length > 0) {
                    // From testing, content type returned for attachment could have things like "text/plain; authoritative=true"
                    // To simplify, let's just log things before ";"
                    const semiColon = contentType.indexOf(';');
                    if (semiColon > 0) {
                        contentType = contentType.substr(0, semiColon);
                    }
                }

                // We only look into first 10 bytes to detect its format for 200 response
                // for not 200, first10Bytes, detectedFormat, mimeMatched will carry a default value of empty
                let status = '';
                let first10Bytes: Uint8Array = null;
                let detectedFormat = '';
                let mimeMatched = false;
                let expectedMime = '';
                if (responseCode == 200) {
                    // detectedFormat carries one of following values:
                    // - ZeroBytes: response is null or 0 in length
                    // - Invalid: response is less than 10 bytes:
                    // - Unknown: format is not in defined list:
                    // - A defined format in defined list
                    const response = request.response;
                    if (!response || response.byteLength == 0) {
                        status = ERROR_IMAGE_ZEROBYTES;
                    } else if (response.byteLength < 10) {
                        status = ERROR_IMAGE_INVALIDLENGTH;
                    } else {
                        first10Bytes = new Uint8Array(request.response, 0, 10);
                        const matchedSig = matchImageSignature(first10Bytes);
                        if (matchedSig) {
                            detectedFormat = matchedSig.format;
                            mimeMatched = contentType && matchedSig.mime.test(contentType);
                            if (!mimeMatched) {
                                status = Constants.ERROR_IMAGE_MISMATCH;
                                expectedMime = matchedSig.expectedMime;
                            }
                        }
                    }
                }

                resolve({
                    status: status,
                    responseCode: responseCode,
                    contentType: contentType,
                    detectedFormat: detectedFormat,
                    first10BytesBase64: first10Bytes ? base64EncodeUint8Array(first10Bytes) : '',
                    mimeMatched: mimeMatched,
                    expectedMime: expectedMime,
                });
            }
        };

        request.ontimeout = function () {
            resolve({ status: ERROR_REQUEST_TIMEOUT });
        };

        request.onerror = function () {
            resolve({ status: ERROR_REQUEST_ERROR });
        };

        request.onabort = function () {
            resolve({ status: ERROR_REQUEST_ABORT });
        };

        if (withCredentials) {
            request.withCredentials = true;
        }

        // IE11 requires setting responseType after the request open otherwise it will throw InvalidState error
        request.open('GET', url, true /*async*/);
        request.responseType = 'arraybuffer';
        request.send();
    });
}

/**
 * Match first 10 bytes against image signature and return matched one
 */
function matchImageSignature(first10Bytes: Uint8Array): ImageSignature {
    for (let i = 0; i < imageSignatures.length; i++) {
        const imageSig = imageSignatures[i];
        for (let j = 0; j < imageSig.signature.length; j++) {
            if (imageSig.signature[j] != first10Bytes[j]) {
                break;
            } else if (j == imageSig.signature.length - 1) {
                // Already reached end of signature
                return imageSig;
            }
        }
    }

    return null;
}

/**
 * Base64 encoding uint8 array
 */
function base64EncodeUint8Array(data: Uint8Array) {
    let str = '';
    for (let i = 0; i < data.length; i++) {
        str += String.fromCharCode(data[i]);
    }
    return btoa(str);
}
